import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/api';
import { FormCard } from '@/components/ui/FormCard';
import { USER_ROLES } from '@/constants';
import LeagueSelectStep from './LeagueSelectStep';
import TournamentDetailsStep from './TournamentDetailsStep';
import styles from './styles.module.scss';

interface AvailableLeague {
  id: number;
  name: string;
  externalId: string;
  country: string | null;
  logoUrl: string | null;
  status: string;
  season: string | null;
}

interface FormData {
  leagueId: number | null;
  name: string;
  description: string;
  isPublic: boolean;
}

// Step config titles will be resolved dynamically using t() inside the component

interface TournamentCreateWizardProps {
  /**
   * League preselected via `/tournaments/create?leagueId=`. When the id is
   * present among the available (tournament-creatable) leagues, the wizard
   * seeds it and opens at step 2. When it is not — the league's season is
   * not active/planned, or the id is stale — the wizard stays at step 1 with
   * nothing selected; no substitute league is invented.
   */
  initialLeagueId?: number;
}

export const TournamentCreateWizard: React.FC<TournamentCreateWizardProps> = ({ initialLeagueId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation('tournament');
  const isAdmin = user?.is_admin === true || user?.role === USER_ROLES.ADMIN;

  const hasAppliedInitialLeagueRef = useRef(false);
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [leagues, setLeagues] = useState<AvailableLeague[]>([]);
  const [leaguesLoading, setLeaguesLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameError, setNameError] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    leagueId: null,
    name: '',
    description: '',
    isPublic: false,
  });

  const loadLeagues = useCallback(async () => {
    try {
      setLeaguesLoading(true);
      const response = await apiClient.getAvailableLeagues();
      const data = response.data?.data || response.data || [];
      setLeagues(data);
    } catch (error) {
      console.error('Failed to load leagues:', error);
      toast.error(t('failedLoadLeagues'));
    } finally {
      setLeaguesLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadLeagues();
  }, [loadLeagues]);

  // Apply the `?leagueId=` preselection once, after the available leagues
  // land. The ref guard keeps a later "Back" to step 1 from being undone.
  useEffect(() => {
    if (hasAppliedInitialLeagueRef.current) return;
    if (!initialLeagueId || leagues.length === 0) return;
    hasAppliedInitialLeagueRef.current = true;
    if (!leagues.some(l => l.id === initialLeagueId)) return;
    setFormData(prev => ({ ...prev, leagueId: initialLeagueId }));
    setCurrentStep(2);
  }, [initialLeagueId, leagues]);

  const selectedLeague = leagues.find(l => l.id === formData.leagueId);

  const handleSelectAndAdvance = (id: number) => {
    setFormData(prev => ({ ...prev, leagueId: id }));
    setCurrentStep(2);
  };

  const handleBack = () => {
    setNameError(false);
    setCurrentStep(1);
  };

  const handleFieldChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'name') setNameError(false);
  };

  const handleSubmit = async () => {
    setNameError(false);

    if (!formData.name.trim()) {
      toast.error(t('tournamentNameRequired'));
      setNameError(true);
      return;
    }

    if (formData.name.trim().length < 3) {
      toast.error(t('tournamentNameMin'));
      setNameError(true);
      return;
    }

    if (!formData.leagueId) {
      toast.error(t('leagueRequired'));
      setCurrentStep(1);
      return;
    }

    try {
      setIsSubmitting(true);

      await apiClient.createUserTournament({
        name: formData.name.trim(),
        description: formData.description.trim(),
        leagueId: formData.leagueId,
        isPublic: formData.isPublic,
      });

      toast.success(t('tournamentCreated'));
      navigate('/tournaments', { replace: true });
    } catch (err: any) {
      let errorMessage = t('errorCreatingTournament');

      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      if (err.response?.status === 403) {
        errorMessage = t('noPublicTournamentPermission');
      } else if (err.response?.status === 400 && err.response?.data?.error?.includes('public tournament')) {
        errorMessage = `A public tournament for this league already exists. ${err.response.data.error}`;
      } else if (err.response?.status === 500 && formData.isPublic) {
        const errorText = err.response?.data?.error || '';
        const leagueName = selectedLeague?.name || 'the selected league';
        if (
          errorText.toLowerCase().includes('duplicate') ||
          errorText.toLowerCase().includes('unique') ||
          errorText.toLowerCase().includes('constraint') ||
          errorText.includes('23505')
        ) {
          errorMessage = t('publicTournamentExistsFor', { league: leagueName });
        } else {
          errorMessage = t('errorCreatingPublicTournament', { league: leagueName });
        }
      }

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepConfig = {
    1: { title: t('selectLeague'), subtitle: t('chooseLeague') },
    2: { title: t('tournamentDetails'), subtitle: t('fillInDetails') },
  };
  const { title, subtitle } = stepConfig[currentStep];

  return (
    <div className={classNames('card', styles.wizardCard)}>
      <FormCard
        title={title}
        subtitle={subtitle}
        titleSize="sm"
        onClose={() => navigate('/tournaments', { replace: true })}
      >
        {/* Step indicator */}
        <div className={styles.stepIndicator}>
          <div
            className={classNames(styles.stepItem, { [styles.stepItemActive]: currentStep === 1, [styles.stepItemCompleted]: currentStep > 1 })}
          >
            <span className={styles.stepNumber}>1</span>
            <span className={styles.stepLabel}>{t('league')}</span>
          </div>
          <div className={styles.stepLine} />
          <div
            className={classNames(styles.stepItem, { [styles.stepItemActive]: currentStep === 2 })}
          >
            <span className={styles.stepNumber}>2</span>
            <span className={styles.stepLabel}>{t('details')}</span>
          </div>
        </div>

        {currentStep === 1 && (
          <LeagueSelectStep
            leagues={leagues}
            selectedLeagueId={formData.leagueId}
            onSelectAndAdvance={handleSelectAndAdvance}
            isLoading={leaguesLoading}
          />
        )}

        {currentStep === 2 && (
          <TournamentDetailsStep
            formData={{
              name: formData.name,
              description: formData.description,
              isPublic: formData.isPublic,
            }}
            onChange={handleFieldChange}
            onBack={handleBack}
            onSubmit={handleSubmit}
            isAdmin={isAdmin}
            isLoading={isSubmitting}
            selectedLeagueName={selectedLeague?.name || ''}
            selectedLeagueLogo={selectedLeague?.logoUrl || null}
            nameError={nameError}
          />
        )}
      </FormCard>
    </div>
  );
};

export default TournamentCreateWizard;
